import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Key, ExternalLink, Check, X, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useOpenRouter } from '@/hooks/useOpenRouter';

// Available models with their display names
const AVAILABLE_MODELS = [
  { id: 'x-ai/grok-4-fast', name: 'Grok 4 Fast' },
  { id: 'google/gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
];

export function ApiKeySettings() {
  const { apiKey, setApiKey, selectedModel, setSelectedModel } = useOpenRouter();
  const [inputValue, setInputValue] = useState(apiKey || '');
  const [showKey, setShowKey] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const handleSave = () => {
    setApiKey(inputValue);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleClear = () => {
    setInputValue('');
    setApiKey('');
  };

  return (
    <Card className="glass-card rounded-2xl overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Key className="h-5 w-5 text-violet-600" />
          OpenRouter API Key
        </CardTitle>
        <CardDescription className="text-sm">
          Configure your OpenRouter API key to use AI features
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="rounded-xl border-violet-200 dark:border-violet-500/20 bg-violet-50/50 dark:bg-violet-950/20">
          <AlertDescription className="text-xs text-muted-foreground">
            <p className="mb-2">
              This app uses OpenRouter to access various AI models for marketing analysis.
            </p>
            <p>
              Get your free API key from{' '}
              <a
                href="https://openrouter.ai/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 font-medium inline-flex items-center gap-1"
              >
                OpenRouter
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="model-select" className="text-xs font-medium">
            AI Model
          </Label>
          <Select value={selectedModel} onValueChange={setSelectedModel}>
            <SelectTrigger id="model-select" className="rounded-xl h-10 text-sm">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_MODELS.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
                    {model.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="api-key" className="text-xs font-medium">
            API Key
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                id="api-key"
                type={showKey ? 'text' : 'password'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="sk-or-v1-..."
                className="rounded-xl h-10 text-sm pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-10 w-10 rounded-xl"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={!inputValue || inputValue === apiKey}
            className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-lg shadow-violet-500/25 rounded-xl h-10 text-sm font-medium"
          >
            {isSaved ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Saved!
              </>
            ) : (
              <>
                <Key className="h-4 w-4 mr-2" />
                Save Key
              </>
            )}
          </Button>
          {apiKey && (
            <Button
              onClick={handleClear}
              variant="outline"
              className="rounded-xl h-10 text-sm"
            >
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {apiKey && (
          <Alert className="rounded-xl border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/20">
            <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <AlertDescription className="text-xs text-emerald-700 dark:text-emerald-300">
              API key configured. You can now use AI features.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
